import axios from "axios";
import toast from "react-hot-toast";
import { handleRequestError } from "../../requests";

// Legacy Bookings.aspx default grids (DisplayBookings).
export const getNewBookings = async () => {
    const response = await axios.get('/Bookings/GetNewBookings').catch(handleRequestError);
    return response.data;
}

export const getRequestedBookings = async () => {
    const response = await axios.get('/Bookings/GetRequestedBookings').catch(handleRequestError);
    return response.data;
}

export const getContainerPendingBookings = async () => {
    const response = await axios.get('/Bookings/GetContainerPendingBookings').catch(handleRequestError);
    return response.data;
}

export const getCompletedBookings = async () => {
    const response = await axios.get('/Bookings/GetCompletedBookings').catch(handleRequestError);
    return response.data;
}

export const getCancelledPendingBookings = async () => {
    const response = await axios.get('/Bookings/GetCancelledPendingBookings').catch(handleRequestError);
    return response.data;
}

export const getMultipleBookings = async () => {
    const response = await axios.get('/Bookings/GetMultipleBookings').catch(handleRequestError);
    return response.data;
}

// "Show Records for last N days" (legacy txtPriorBookingFilter / txtCancelledConfirmedBookingFilter).
export const DEFAULT_BOOKING_DAYS = 30;

const daysParams = (days) => {
    const value = days === undefined || days === null ? String(DEFAULT_BOOKING_DAYS) : String(days).trim();
    const n = Number(value);
    if (value === '' || !Number.isInteger(n) || n < 0 || n > 9999) {
        const message = 'Filter value must be between 0 and 9999 days';
        toast.error(message);
        throw new Error(message);
    }
    // Legacy first load bounds CreatedOn by Now + 1 year; the grid's Display button by Now.
    return { days: n, display: n !== DEFAULT_BOOKING_DAYS };
}

export const getPriorBookings = async (params) => {
    const response = await axios.get('/Bookings/GetPriorBookings', { params: daysParams(params?.days) }).catch(handleRequestError);
    return response.data;
}

export const getCancelledConfirmedBookings = async (params) => {
    const response = await axios.get('/Bookings/GetCancelledConfirmedBookings', { params: daysParams(params?.days) }).catch(handleRequestError);
    return response.data;
}

// Legacy "Filter Bookings" panel (status + created date range).
export const searchBookings = async (params) => {
    if (!params?.startDate || !params?.endDate) {
        const message = 'Please select date range to display bookings.';
        toast.error(message);
        throw new Error(message);
    }
    const response = await axios.get('/Bookings/SearchBookings', {
        params: { status: params.status, startDate: params.startDate, endDate: params.endDate },
    }).catch(handleRequestError);
    return response.data;
}

// Several dropdowns on the add page read the same lookups; share one request per page load.
let lookupsCache: { at: number, promise: Promise<any> } | null = null;
export const getBookingLookups = () => {
    if (!lookupsCache || Date.now() - lookupsCache.at > 60 * 1000) {
        const promise = axios.get('/Bookings/GetBookingLookups').catch(handleRequestError).then((r) => r.data);
        promise.catch(() => { lookupsCache = null; });
        lookupsCache = { at: Date.now(), promise };
    }
    return lookupsCache.promise;
}

export const getConsigneeDefaults = async (customerId) => {
    const response = await axios.get(`/Bookings/GetConsigneeDefaults/${customerId}`).catch(handleRequestError);
    return response.data;
}

export const getBookingOrderPrefill = async (orderId) => {
    const response = await axios.get(`/Bookings/GetOrderPrefill/${orderId}`).catch(handleRequestError);
    return response.data;
}

const toInt = (v) => {
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? 0 : n;
}

export const insertBooking = async (booking) => {
    const payload = {
        ...booking,
        customerId: toInt(booking.customerId),
        usPort: toInt(booking.usPort),
        customerPort: toInt(booking.customerPort),
    };
    const response = await axios.post('/Bookings/InsertBooking', payload).catch((error) => {
        const data = error?.response?.data;
        toast.error(typeof data === 'string' && data ? data : 'Failed to Save');
        throw data ?? error;
    });
    return response.data;
}

const toastAndThrow = (fallback: string) => (error) => {
    const data = error?.response?.data;
    toast.error(typeof data === 'string' && data ? data : fallback);
    throw data ?? error;
}

// Legacy Bookings_Edit.aspx.
export const getBookingForEdit = async (bookingId) => {
    const response = await axios.get(`/Bookings/GetBookingForEdit/${bookingId}`).catch(handleRequestError);
    return response.data;
}

// Lookup lists returned with the booking; not part of the posted form.
const EDIT_LOOKUP_KEYS = ['customers', 'containers', 'usPorts', 'customerPorts', 'departurePorts', 'returnPorts', 'bookingAgents', 'returnRegions'];

export const updateBooking = async (booking) => {
    const payload = { ...booking };
    EDIT_LOOKUP_KEYS.forEach((key) => delete payload[key]);
    ['customerId', 'usPort', 'customerPort', 'customerDeparturePort', 'customerUsReturnPort'].forEach((key) => {
        payload[key] = toInt(booking[key]);
    });
    const response = await axios.post(`/Bookings/UpdateBooking/${booking.bookingId}`, payload).catch(toastAndThrow('Failed to Save'));
    return response.data;
}

export const composeBookingNotification = async (bookingId, form) => {
    const response = await axios.post(`/Bookings/ComposeNotification/${bookingId}`, {
        customerId: toInt(form.customerId),
        dateRequested: form.dateRequested || null,
        bookingAgentSelect: form.bookingAgentSelect,
        bookingAgentOther: form.bookingAgentOther,
    }).catch(toastAndThrow('Unable to prepare the notification.'));
    return response.data;
}

export const sendBookingNotification = async (bookingId, message) => {
    const response = await axios.post(`/Bookings/SendNotification/${bookingId}`, message).catch(toastAndThrow('Failed to send.'));
    return response.data;
}

export const notifyBookingStatusOnly = async (bookingId) => {
    const response = await axios.post(`/Bookings/NotifyStatusOnly/${bookingId}`).catch(toastAndThrow('Failed to update the notify status.'));
    return response.data;
}

export const getBookingAudit = async (bookingId) => {
    const response = await axios.get(`/Bookings/GetBookingAudit/${bookingId}`).catch(handleRequestError);
    return response.data;
}
